class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this._boundCloseOverlay = this.close.bind(this);
    this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this._boundCloseOverlay);
    this.setHeaderCartIconAccessibility();
    this._boundHandleDocumentKeyDown = this._handleDocumentKeyDown.bind(this);
    // Delegated: the close button lives inside #CartDrawer, whose innerHTML is
    // replaced on every cart re-render. Binding to the button directly loses the
    // listener after the first add-to-cart, so delegate from the persistent host.
    this._boundCloseContinueShopping = (event) => {
      if (event.target.closest('.drawer-cta--close')) this.close();
    };
  }

  connectedCallback() {
    this.addEventListener('click', this._boundCloseContinueShopping);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this._boundCloseContinueShopping);
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (getComputedStyle(this).display === 'none') return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);

    setTimeout(() => {
      this.classList.add('animate', 'active');
    }, 30);

    document.body.classList.add('cart-drawer-open');

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        if (typeof trapFocus === 'function') {
            trapFocus(containerToTrapFocusOn, focusElement);
        }
      },
      { once: true }
    );

    document.addEventListener('keydown', this._boundHandleDocumentKeyDown);
  }

  close() {
    if (!this.classList.contains('active')) return;

    this.classList.remove('active');
    document.body.classList.remove('cart-drawer-open');
    if (typeof removeTrapFocus === 'function' && this.activeElement) {
        removeTrapFocus(this.activeElement);
    }
    document.removeEventListener('keydown', this._boundHandleDocumentKeyDown);
  }

  _handleDocumentKeyDown(event) {
    if (event.code === 'Escape') {
      if (this.classList.contains('active')) {
        const isModalDrawerOpen = document.querySelector('modal-drawer[open]');
        if (isModalDrawerOpen) {
          return;
        }
        event.preventDefault();
        this.close();
      }
    }
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    if (typeof onKeyUpEscape === 'function') {
        cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
    }
  }

  renderContents(parsedState) {
    this.classList.remove('is-empty');
    const drawerInner = this.querySelector('.drawer__inner');
    if (drawerInner && drawerInner.classList.contains('is-empty')) {
        drawerInner.classList.remove('is-empty');
    }
    this.productId = parsedState.id;

    /* Shopify can answer a cart AJAX request with `sections: null` on an
       otherwise successful 200 that DID add the line item. Observed in
       production on atomic-shopify 2026-08-31/09-01 across every market
       except the visitor's own, template-independent, and resolving on its
       own with no merchant-side change. A section id that does not exist
       returns the same null, so it is not one bad section, and the GET
       Section Rendering API kept working throughout.

       Without this guard the dereference below throws "Cannot read
       properties of null (reading 'cart-drawer')", the drawer never opens,
       and an add-to-cart that HAS SUCCEEDED looks broken to the shopper —
       who then clicks again and adds a duplicate. Fall back to fetching each
       section directly instead of throwing. */
    if (!parsedState.sections) {
      this.renderContentsFromSectionApi();
      return;
    }

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      if (sectionElement) {
        const html = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
        if (html !== null) sectionElement.innerHTML = html;
      }
    });

    this.openAfterRender();
  }

  /* Same contract as renderContents, sourced from the GET Section Rendering
     API. Each section is fetched independently so one failure cannot block
     the others, and the drawer opens either way — the item is already in the
     cart, so refusing to open would hide a successful add.

     Armada has no global getSectionsUrl() helper (atomic does), so the
     current path is used directly. */
  async renderContentsFromSectionApi() {
    const base = window.location.pathname;

    await Promise.all(
      this.getSectionsToRender().map(async (section) => {
        const target = section.selector
          ? document.querySelector(section.selector)
          : document.getElementById(section.id);
        if (!target) return;

        /* no-store: a cached copy would hand back the very markup being
           replaced. */
        const html = await fetch(`${base}?section_id=${section.id}`, { cache: 'no-store' })
          .then((r) => (r.ok ? r.text() : null))
          .catch(() => null);
        if (!html) return;

        const inner = this.getSectionInnerHTML(html, section.selector);
        if (inner !== null) target.innerHTML = inner;
      })
    );

    this.openAfterRender();
  }

  openAfterRender() {
    setTimeout(() => {
      const overlay = this.querySelector('#CartDrawer-Overlay');
      overlay?.removeEventListener('click', this._boundCloseOverlay);
      overlay?.addEventListener('click', this._boundCloseOverlay);
      this.open();
    }, 50);
  }

  /* Returns null rather than throwing: `html` may be undefined when Shopify
     omits a requested section, and querySelector may miss when the markup
     comes back as an error page. Either used to take down the whole
     add-to-cart. */
  getSectionInnerHTML(html, selector = '.shopify-section') {
    if (!html) return null;
    const el = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return el ? el.innerHTML : null;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

function normalizeCartMessage(payload) {
  if (typeof payload === 'string') return payload.trim();
  if (!payload || typeof payload !== 'object') return '';

  if (typeof payload.description === 'string') return payload.description.trim();
  if (typeof payload.message === 'string') return payload.message.trim();

  if (typeof payload.errors === 'string') return payload.errors.trim();
  if (payload.errors && typeof payload.errors === 'object') {
    return Object.values(payload.errors)
      .flat()
      .filter((value) => typeof value === 'string' && value.trim())
      .join(' ')
      .trim();
  }

  return '';
}

function announceCartDrawerStatus(message) {
  const cartStatus = document.getElementById('CartDrawer-LiveRegionText');
  if (!cartStatus || !message) return;

  cartStatus.textContent = message;
  cartStatus.setAttribute('aria-hidden', 'false');
  setTimeout(() => cartStatus.setAttribute('aria-hidden', 'true'), 1000);
}

function showCartErrorToast(message) {
  const text = normalizeCartMessage(message);
  if (!text) return;

  announceCartDrawerStatus(text);

  const toast = document.querySelector('cart-error-toast');
  if (toast && typeof toast.show === 'function') {
    toast.show(text);
    return;
  }

  publish(PUB_SUB_EVENTS.cartError, { source: 'cart-drawer-items', message: text });
}

class CartDrawerItems extends CartItems {
  get immediateQuantityUpdates() {
    return true;
  }

  getResolvedLineQuantity(line, parsedState, variantId) {
    if (variantId && parsedState.items?.length) {
      const matchedItem = parsedState.items.find(
        (item) => String(item.variant_id) === String(variantId)
      );
      if (matchedItem) return matchedItem.quantity;
    }

    const fromState = parsedState.items?.[line - 1]?.quantity;
    if (fromState !== undefined) return fromState;

    const fromDom = document.getElementById(`Drawer-quantity-${line}`);
    if (fromDom) {
      const qty = parseInt(fromDom.value, 10);
      if (!Number.isNaN(qty)) return qty;
    }

    const sectionHtml = parsedState.sections?.['cart-drawer'];
    if (!sectionHtml) return undefined;

    const doc = new DOMParser().parseFromString(sectionHtml, 'text/html');
    const fromSection = doc.getElementById(`Drawer-quantity-${line}`);
    if (!fromSection) return undefined;

    const qty = parseInt(fromSection.value, 10);
    return Number.isNaN(qty) ? undefined : qty;
  }

  getLineQuantityCap(line) {
    const input = document.getElementById(`Drawer-quantity-${line}`);
    if (!input) return null;

    const quantityInput = input.closest('quantity-input');
    if (quantityInput?.getEffectiveMax) {
      return quantityInput.getEffectiveMax(input);
    }

    let max = null;
    const maxAttr = input.getAttribute('max');
    if (maxAttr !== null && maxAttr !== '') {
      const ruleMax = parseInt(maxAttr, 10);
      if (!Number.isNaN(ruleMax)) max = ruleMax;
    }

    const inventoryAttr = input.dataset.inventoryQuantity;
    if (inventoryAttr !== undefined && inventoryAttr !== '') {
      const inventory = parseInt(inventoryAttr, 10);
      const cartQty = parseInt(input.dataset.cartQuantity, 10) || 0;
      const lineQty = parseInt(input.value, 10) || 0;
      if (!Number.isNaN(inventory)) {
        const inventoryMax = inventory - (cartQty - lineQty);
        max = max === null ? inventoryMax : Math.min(max, inventoryMax);
      }
    }

    return max;
  }

  isIncrementAtCap(name, requestedQty, resolvedQty, line) {
    if (name !== 'plus' || resolvedQty === undefined || Number.isNaN(requestedQty)) return false;

    const cap = this.getLineQuantityCap(line);
    return cap !== null && resolvedQty >= cap && requestedQty >= resolvedQty;
  }

  shouldShowQuantityLimitMessage(name, requestedQty) {
    return name === 'plus' && requestedQty > 0;
  }

  buildQuantityLimitMessage(quantity) {
    return window.cartStrings.quantityError.replace('[quantity]', quantity);
  }

  finalizeLineQuantityState(line, resolvedQty, requestId, { delayPlusDisable = false } = {}) {
    this.updateLineQuantityValue(line, resolvedQty);

    const applyRules = () => {
      if (requestId !== this._quantityRequestSeq) return;
      this._suppressPlusDisable = false;
      this.applyLineQuantityRules(line);
    };

    if (delayPlusDisable) {
      setTimeout(applyRules, CART_QUANTITY_LIMIT_DISABLE_DELAY);
      return;
    }

    applyRules();
    requestAnimationFrame(() => {
      applyRules();
      requestAnimationFrame(applyRules);
    });
  }

  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }

  updateQuantity(line, quantity, name, variantId) {
    const requestId = (this._quantityRequestSeq = (this._quantityRequestSeq || 0) + 1);
    this._suppressPlusDisable = true;
    this.enableLoading(line);

    const sections = ['cart-drawer', 'cart-icon-bubble'];
    const body = JSON.stringify({
      line,
      quantity,
      sections,
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) =>
        response.text().then((state) => ({
          response,
          state,
        }))
      )
      .then(({ response, state }) => {
        if (requestId !== this._quantityRequestSeq) return;

        const parsedState = JSON.parse(state);
        const quantityElement = document.getElementById(`Drawer-quantity-${line}`);
        const requestedQty = parseInt(quantity, 10);
        const cartDrawerWrapper = document.querySelector('cart-drawer');
        const isErrorResponse = !response.ok || parsedState.status === 422 || parsedState.status === '422';
        const hasHardError =
          typeof parsedState.errors === 'string' ||
          (parsedState.errors &&
            typeof parsedState.errors === 'object' &&
            Object.keys(parsedState.errors).length > 0);
        const hasLimitResponse =
          isErrorResponse || Boolean(parsedState.description) || Boolean(parsedState.message);

        if (hasHardError && !parsedState.description && !parsedState.message) {
          if (quantityElement) {
            quantityElement.value = quantityElement.getAttribute('value');
          }
          showCartErrorToast(parsedState.errors);
          this.finalizeLineQuantityState(
            line,
            this.getResolvedLineQuantity(line, parsedState, variantId),
            requestId,
            { delayPlusDisable: true }
          );
          return;
        }

        if (parsedState.sections?.['cart-drawer']) {
          this.classList.toggle('is-empty', parsedState.item_count === 0);
          if (cartDrawerWrapper) {
            cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);
          }
          this.patchCartDrawerContents(parsedState, line, quantity);
        } else if (hasLimitResponse && quantityElement) {
          const serverQty = parsedState.items?.[line - 1]?.quantity;
          quantityElement.value = serverQty ?? quantityElement.getAttribute('value');
        }

        if (requestedQty === 0) {
          if (parsedState.item_count === 0 && cartDrawerWrapper) {
            trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'));
          }

          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId });
          return;
        }

        const resolvedQty = this.getResolvedLineQuantity(line, parsedState, variantId);
        let message = normalizeCartMessage(parsedState);

        if (this.shouldShowQuantityLimitMessage(name, requestedQty)) {
          if (
            !message &&
            resolvedQty !== undefined &&
            resolvedQty !== requestedQty
          ) {
            message = this.buildQuantityLimitMessage(resolvedQty);
          }

          if (!message && this.isIncrementAtCap(name, requestedQty, resolvedQty, line)) {
            message = this.buildQuantityLimitMessage(resolvedQty);
          }
        }

        if (!message && isErrorResponse && this.shouldShowQuantityLimitMessage(name, requestedQty)) {
          message = window.cartStrings.error;
        }

        const shouldDelayPlusDisable = Boolean(message);

        if (message) {
          showCartErrorToast(message);
        }

        this.finalizeLineQuantityState(line, resolvedQty, requestId, { delayPlusDisable: shouldDelayPlusDisable });

        const lineItem = document.getElementById(`CartDrawer-Item-${line}`);
        if (lineItem && name && lineItem.querySelector(`[name="${name}"]`)) {
          trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`));
        } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'));
        } else if (this.querySelector('.cart-item') && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper, this.querySelector('.cart-item__name'));
        }

        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId });
      })
      .catch(() => {
        if (requestId !== this._quantityRequestSeq) return;

        this.querySelectorAll('.loading__spinner').forEach((overlay) => overlay.classList.add('hidden'));
        showCartErrorToast(window.cartStrings.error);
        this.finalizeLineQuantityState(line, this.getResolvedLineQuantity(line, {}), requestId, {
          delayPlusDisable: true,
        });
      })
      .finally(() => {
        if (requestId !== this._quantityRequestSeq) return;

        if (this.isConnected) {
          this.disableLoading(line);
        }
      });

    document.dispatchEvent(
      new CustomEvent('cartUpdated', {
        detail: { message: 'Cart was updated' },
      })
    );
  }

  patchCartDrawerContents(parsedState, line, quantity) {
    /* The `if (!drawerHtml)` guard below is too late — reading ['cart-drawer']
       off a null `sections` throws first. See renderContents above. */
    if (!parsedState.sections) return;
    const drawerHtml = parsedState.sections['cart-drawer'];
    if (!drawerHtml) return;

    const doc = new DOMParser().parseFromString(drawerHtml, 'text/html');
    const lineNum = parseInt(line, 10);
    const isRemove = parseInt(quantity, 10) === 0;

    if (parsedState.item_count === 0) {
      this.replaceDrawerInner(doc);
      this.updateCartIconBubble(parsedState);
      return;
    }

    const targetItem = document.getElementById(`CartDrawer-Item-${lineNum}`);
    const sourceItem = doc.getElementById(`CartDrawer-Item-${lineNum}`);

    if (isRemove || !targetItem || !sourceItem) {
      this.replaceCartItemsList(doc);
    } else {
      this.patchCartLineItem(targetItem, sourceItem, lineNum);
    }

    this.patchFreeShippingBar(doc);
    this.patchCartDrawerFooter(doc);
    this.updateDrawerCountBadge(parsedState.item_count);
    this.updateCartIconBubble(parsedState);
  }

  replaceDrawerInner(doc) {
    const target = document.querySelector('#CartDrawer .drawer__inner');
    const source = doc.querySelector('.drawer__inner');
    if (target && source) {
      target.innerHTML = source.innerHTML;
    }
  }

  replaceCartItemsList(doc) {
    const target = this.querySelector('.cart-items__list');
    const source = doc.querySelector('.cart-items__list');
    if (target && source) {
      target.innerHTML = source.innerHTML;
    }
  }

  patchCartLineItem(targetItem, sourceItem, line) {
    const targetBottom = targetItem.querySelector('.cart-item__bottom');
    const sourceBottom = sourceItem.querySelector('.cart-item__bottom');
    if (targetBottom && sourceBottom) {
      targetBottom.innerHTML = sourceBottom.innerHTML;
    }
  }

  patchFreeShippingBar(doc) {
    const target = this.querySelector('free-shipping-progress-bar');
    const source = doc.querySelector('free-shipping-progress-bar');
    if (target && source) {
      target.replaceWith(source);
    }
  }

  patchCartDrawerFooter(doc) {
    const target = document.querySelector('.cart-drawer__footer');
    const source = doc.querySelector('.cart-drawer__footer');
    if (target && source) {
      target.innerHTML = source.innerHTML;
    }
  }

  updateDrawerCountBadge(itemCount) {
    const badge = document.querySelector('.drawer__count-badge span');
    if (badge) {
      badge.textContent = itemCount;
    }
  }

  updateCartIconBubble(parsedState) {
    /* Same too-late-guard trap as patchCartDrawerContents. */
    if (!parsedState.sections) return;
    const bubbleHtml = parsedState.sections['cart-icon-bubble'];
    const bubbleContainer = document.getElementById('cart-icon-bubble');
    if (!bubbleHtml || !bubbleContainer) return;

    bubbleContainer.innerHTML = this.getSectionInnerHTML(bubbleHtml, '.shopify-section');
  }

  updateLiveRegions(line, message) {
    const text = normalizeCartMessage(message);
    if (text) {
      showCartErrorToast(text);
    }

    this.lineItemStatusElement?.setAttribute('aria-hidden', 'true');
  }

  refreshCartDrawerLine(line) {
    return fetch(`${routes.cart_url}?section_id=cart-drawer`)
      .then((response) => response.text())
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const targetItem = document.getElementById(`CartDrawer-Item-${line}`);
        const sourceItem = doc.getElementById(`CartDrawer-Item-${line}`);

        if (targetItem && sourceItem) {
          this.patchCartLineItem(targetItem, sourceItem, line);
          this.syncLineQuantityState(line, undefined, this._quantityRequestSeq);
          return;
        }

        this.replaceCartItemsList(doc);
      });
  }

  updateLineQuantityValue(line, serverQty) {
    const lineItem = document.getElementById(`CartDrawer-Item-${line}`);
    const input = lineItem?.querySelector('.quantity__input');
    if (!input) return;

    const resolvedQty = serverQty ?? parseInt(input.value, 10);
    if (!Number.isNaN(resolvedQty)) {
      input.value = resolvedQty;
      input.setAttribute('value', String(resolvedQty));
    }
  }

  applyLineQuantityRules(line) {
    const lineItem = document.getElementById(`CartDrawer-Item-${line}`);
    const input = lineItem?.querySelector('.quantity__input');
    if (!input) return;

    const quantityInput = input.closest('quantity-input');
    if (quantityInput?.validateQtyRules) {
      quantityInput.validateQtyRules();
      return;
    }

    customElements.whenDefined('quantity-input').then(() => {
      input.closest('quantity-input')?.validateQtyRules?.();
    });
  }

  syncLineQuantityState(line, serverQty, requestId, { delayPlusDisable = false } = {}) {
    this.updateLineQuantityValue(line, serverQty);

    const applyRules = () => {
      if (requestId !== undefined && requestId !== this._quantityRequestSeq) return;
      this.applyLineQuantityRules(line);
    };

    if (delayPlusDisable) {
      setTimeout(applyRules, CART_QUANTITY_LIMIT_DISABLE_DELAY);
      return;
    }

    applyRules();
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

/* Refresh the cart drawer when a page is shown — port of the Atomic fix (AAS-5514).

   The bug: add to cart, press Back, open the cart. The drawer shows the cart as
   it was BEFORE the add — sometimes several states stale — while the server cart
   is correct. Reproduced on qual: server held 1 item while the drawer rendered 2
   lines and its count badge read 4.

   Chrome answers Back from a cache (bfcache, or the HTTP disk cache) rather than
   re-rendering, so the drawer markup is whatever Liquid produced on the earlier
   visit. Nothing in the theme corrects it: CartDrawer.open() only animates and
   traps focus, and onCartUpdate() runs on cart mutations, which a Back is not.

   NOT GATED ON THE NAVIGATION TYPE. The obvious guard — pageshow.persisted, or a
   "back_forward" navigation type — is unreliable: on Atomic a restored page
   reported type "navigate" with a non-zero transferSize while its markup was
   demonstrably stale. Compare against the cart itself instead and touch the DOM
   only on a real disagreement. A matching cart costs one small uncached
   /cart.js per page show and nothing else.

   NOT REUSING onCartUpdate(). That replaces `cart-drawer-items` and
   `.cart-drawer__footer` only, which leaves the drawer header's
   .drawer__count-badge showing the old number — one of the visible symptoms
   here. Swapping #CartDrawer wholesale covers the badge, the item list and the
   footer in one go.

   NOT SKIPPED WHEN THE DRAWER IS OPEN. A bfcache restore hands the DOM back as
   it was left, so a drawer opened before navigating away comes back already
   open holding pre-add contents — the case that most needs refreshing. Only
   #CartDrawer's children are replaced, so the host keeps its `active` class and
   the drawer stays open through the update. */
(() => {
  let inFlight = false;

  /* Total units, not line count: a line count alone misses a re-add of the same
     variant, where the cart still has one line but the rendered quantity is a
     step behind. `.quantity__input` is the visible stepper — one per line. */
  const renderedUnits = (drawer) =>
    [...drawer.querySelectorAll('.cart-item')].reduce((total, item) => {
      const input = item.querySelector('.quantity__input');
      const qty = input ? parseInt(input.value, 10) : NaN;
      return total + (Number.isNaN(qty) ? 1 : qty);
    }, 0);

  const reconcile = async () => {
    const drawer = document.querySelector('cart-drawer');
    const target = document.getElementById('CartDrawer');
    if (!drawer || !target || inFlight) return;

    inFlight = true;
    try {
      const cartUrl = typeof routes !== 'undefined' && routes.cart_url ? routes.cart_url : '/cart';
      /* cache: 'no-store' is load-bearing on both requests. The reason this runs
         is that the browser answered from its disk cache; these are served from
         that same cache unless told otherwise, handing back the very markup
         being replaced. */
      const cart = await fetch(`${cartUrl}.js`, { cache: 'no-store' })
        .then((r) => (r.ok ? r.json() : null))
        .catch(() => null);
      if (!cart || typeof cart.item_count !== 'number') return;
      if (cart.item_count === renderedUnits(drawer)) return;

      const html = await fetch(`${cartUrl}?section_id=cart-drawer`, { cache: 'no-store' })
        .then((r) => (r.ok ? r.text() : null))
        .catch(() => null);
      if (!html) return;

      const fresh = new DOMParser().parseFromString(html, 'text/html');
      const freshInner = fresh.getElementById('CartDrawer');
      if (!freshInner) return;
      target.innerHTML = freshInner.innerHTML;

      /* `is-empty` is rendered onto BOTH <cart-drawer> and <cart-drawer-items>
         (snippets/cart-drawer.liquid). Only the second sits inside #CartDrawer
         and came across in the swap, so the host class is mirrored by hand. */
      const freshItems = fresh.querySelector('cart-drawer-items');
      if (freshItems) drawer.classList.toggle('is-empty', freshItems.classList.contains('is-empty'));
    } finally {
      inFlight = false;
    }
  };

  window.addEventListener('pageshow', reconcile);
})();
